import { AvailabilityType } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import {
  getWeeklyAvailabilitySummary,
  type MenuAvailability,
} from '@wix/restaurants-olo-operations-client-commons';
import type { TFunction } from '@wix/yoshi-flow-editor';
import { DateTime } from 'luxon';

import { DEFAULT_TIMEZONE } from '../constants';
import { formatTimeOfDayRange, toZonedDateTime } from '../time';

import type {
  AvailabilityStatusTextAvailability,
  AvailabilityStatusTextKeys,
  AvailabilityStatusTextParams,
  WeeklyScheduleOptions,
} from './availabilityTypes';
import { weeklyAvailabilityDayByAvailableTimesDay } from './weeklyScheduleAvailability';

export const menuAvailabilityStatusKeys = {
  hasNextAvailabilityKeys: {
    dateRangeText: 'menu_olo.menuAvailabilityStatus.dateRange.text',
    dateText: 'menu_olo.menuAvailabilityStatus.oneDate.text',
    weeklyScheduleText: 'menu_olo.menuAvailabilityStatus.weeklySchedule.text',
  },
  noNextAvailabilityKeys: {
    dateRangeText:
      'menu_olo.menuAvailabilityStatus.dateRange.noNextAvailability.text',
    dateText: 'menu_olo.menuAvailabilityStatus.oneDate.noNextAvailability.text',
    weeklyScheduleText:
      'menu_olo.menuAvailabilityStatus.weeklySchedule.noNextAvailability.text',
  },
};

export const sectionAvailabilityStatusKeys = {
  dateRangeText: 'section.sectionAvailabilityStatus.dateRange.text',
  dateText: 'section.sectionAvailabilityStatus.oneDate.text',
  weeklyScheduleText: 'section.sectionAvailabilityStatus.weeklySchedule.text',
};

const itemModalAvailabilityStatusKeys = {
  nextAvailableTimeslotText:
    'itemModal.menuAvailabilityStatus.nextAvailableTimeslot.canUpdateTime.text',
  noNextAvailabilityKeys: {
    dateRangeText:
      'itemModal.menuAvailabilityStatus.dateRange.noNextAvailability.text',
    dateText:
      'itemModal.menuAvailabilityStatus.oneDate.noNextAvailability.text',
    weeklyScheduleText:
      'itemModal.menuAvailabilityStatus.weeklySchedule.noNextAvailability.text',
  },
  availabilityConflictText:
    'itemModal.menuAvailabilityStatus.availabilityConflict.noNextAvailability.text',
};

const AvailabilityDataType = {
  TimestampRanges: 'timestamp_ranges',
  WeeklySchedule: 'weekly_schedule',
  None: 'none',
} as const;

type AvailabilityData =
  | {
      type: typeof AvailabilityDataType.TimestampRanges;
      startTime: Date;
      endTime: Date;
    }
  | {
      type: typeof AvailabilityDataType.WeeklySchedule;
      weeklyScheduleOptions: WeeklyScheduleOptions;
    }
  | { type: typeof AvailabilityDataType.None };

const extractAvailabilityData = (
  availability: AvailabilityStatusTextAvailability,
  timezone = DEFAULT_TIMEZONE,
): AvailabilityData => {
  if (!availability) {
    return { type: AvailabilityDataType.None };
  }

  const zone = timezone || DEFAULT_TIMEZONE;

  if (availability.type === AvailabilityType.TIMESTAMP_RANGES) {
    const ranges = availability.timestampRangesOptions?.ranges;

    if (!ranges?.length) {
      return { type: AvailabilityDataType.None };
    }

    const today = DateTime.local({ zone }).startOf('day').toMillis();
    const range = ranges.find((candidate) => {
      const endTime = toZonedDateTime(candidate.endTime ?? undefined, zone);

      return endTime ? endTime.toMillis() >= today : false;
    });

    const startTime = toZonedDateTime(range?.startTime ?? undefined, zone);
    const endTime = toZonedDateTime(range?.endTime ?? undefined, zone);

    if (!startTime || !endTime) {
      return { type: AvailabilityDataType.None };
    }

    return {
      type: AvailabilityDataType.TimestampRanges,
      startTime: startTime.toJSDate(),
      endTime: endTime.toJSDate(),
    };
  }

  if (availability.type === AvailabilityType.WEEKLY_SCHEDULE) {
    const weeklyScheduleOptions = availability.weeklyScheduleOptions as
      | WeeklyScheduleOptions
      | undefined;

    return weeklyScheduleOptions?.weeklyAvailability ||
      weeklyScheduleOptions?.availableTimes?.length
      ? { type: AvailabilityDataType.WeeklySchedule, weeklyScheduleOptions }
      : { type: AvailabilityDataType.None };
  }

  return { type: AvailabilityDataType.None };
};

const getWeeklyAvailabilitySummaryText = ({
  weeklyScheduleOptions,
  locale,
  t,
}: {
  weeklyScheduleOptions: WeeklyScheduleOptions;
  locale: string;
  t: TFunction;
}): string => {
  const weeklyAvailability = weeklyScheduleOptions?.weeklyAvailability;

  if (!weeklyAvailability) {
    return (
      weeklyScheduleOptions?.availableTimes
        ?.map((daySchedule) => {
          const day = daySchedule.dayOfWeek
            ? weeklyAvailabilityDayByAvailableTimesDay[daySchedule.dayOfWeek]
            : undefined;
          const dayLabel = day
            ? t(`menu_olo.menuAvailabilityStatus.${day}.short`)
            : '';
          const slots =
            daySchedule.timeRanges
              ?.map((range) => formatTimeOfDayRange({ range, locale }))
              .filter(Boolean) ?? [];

          return dayLabel && slots.length
            ? `${dayLabel}: ${slots.join(', ')}`
            : '';
        })
        .filter(Boolean)
        .join('; ') ?? ''
    );
  }

  const summary = getWeeklyAvailabilitySummary({
    availability: weeklyAvailability,
    dayNameProvider: (day) => t(`menu_olo.menuAvailabilityStatus.${day}.short`),
    languageCode: locale,
  });

  if (!summary) {
    return '';
  }

  return Array.from(summary.entries())
    .map(([days, timeSlots]) => {
      const slots = timeSlots.map((slot) => slot.timeSlot);

      return slots.length ? `${days}: ${slots.join(', ')}` : '';
    })
    .filter(Boolean)
    .join('; ');
};

const formatDateRangeText = ({
  startTime,
  endTime,
  locale,
  timezone,
  keys,
  t,
}: {
  startTime: Date;
  endTime: Date;
  locale: string;
  timezone: string;
  keys: AvailabilityStatusTextKeys;
  t: TFunction;
}): string => {
  const dateFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: timezone,
  } as const;
  const startDate = startTime.toLocaleString(locale, dateFormatOptions);
  const endDate = endTime.toLocaleString(locale, dateFormatOptions);
  const key = startDate === endDate ? keys.dateText : keys.dateRangeText;

  return t(key, { startDate, endDate });
};

export const getAvailabilityStatusText = ({
  availability,
  keys,
  locale,
  timezone = DEFAULT_TIMEZONE,
  t,
}: AvailabilityStatusTextParams): string => {
  const data = extractAvailabilityData(availability, timezone);

  if (data.type === AvailabilityDataType.TimestampRanges) {
    return formatDateRangeText({
      startTime: data.startTime,
      endTime: data.endTime,
      locale,
      timezone,
      keys,
      t,
    });
  }

  if (data.type === AvailabilityDataType.WeeklySchedule) {
    const weeklyAvailabilitySummary = getWeeklyAvailabilitySummaryText({
      weeklyScheduleOptions: data.weeklyScheduleOptions,
      locale,
      t,
    });

    return weeklyAvailabilitySummary
      ? t(keys.weeklyScheduleText, { weeklyAvailabilitySummary })
      : '';
  }

  return '';
};

export const getMenuAvailabilityStatusText = ({
  availability,
  hasNextAvailability,
  locale,
  timezone = DEFAULT_TIMEZONE,
  t,
}: {
  availability: MenuAvailability | undefined;
  hasNextAvailability: boolean;
  locale: string;
  timezone?: string;
  t: TFunction;
}): string =>
  getAvailabilityStatusText({
    availability,
    keys: hasNextAvailability
      ? menuAvailabilityStatusKeys.hasNextAvailabilityKeys
      : menuAvailabilityStatusKeys.noNextAvailabilityKeys,
    locale,
    timezone,
    t,
  });

export const getItemModalAvailabilityStatusText = ({
  availability,
  nextAvailableTimeSlot,
  locale,
  timezone = DEFAULT_TIMEZONE,
  t,
}: {
  availability: AvailabilityStatusTextAvailability;
  nextAvailableTimeSlot?: {
    timeSlot?: {
      startTime?: Date | string | null;
    } | null;
  };
  locale: string;
  timezone?: string;
  t: TFunction;
}): string => {
  const nextStartTimeValue =
    nextAvailableTimeSlot?.timeSlot?.startTime ?? undefined;
  const nextStartTime = nextStartTimeValue
    ? (nextStartTimeValue instanceof Date
        ? DateTime.fromJSDate(nextStartTimeValue)
        : DateTime.fromISO(nextStartTimeValue)
      ).setZone(timezone)
    : undefined;

  if (nextStartTime?.isValid) {
    const startTime = nextStartTime.setLocale(locale).toLocaleString({
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });

    return t(itemModalAvailabilityStatusKeys.nextAvailableTimeslotText, {
      startTime,
    });
  }

  const availabilityText = getAvailabilityStatusText({
    availability,
    keys: itemModalAvailabilityStatusKeys.noNextAvailabilityKeys,
    locale,
    timezone,
    t,
  });

  return (
    availabilityText ||
    t(itemModalAvailabilityStatusKeys.availabilityConflictText)
  );
};
